import { Drawer } from '@base-ui/react/drawer'
import type { ReactNode } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'

interface SideDrawerProps extends Pick<Drawer.Popup.Props, 'initialFocus' | 'finalFocus'> {
  children: ReactNode
  className?: string
  onClose: () => void
  side: 'left' | 'right'
  underNavbar?: boolean
}

function SideDrawer({
  children,
  className,
  onClose,
  side,
  underNavbar,
  initialFocus,
  finalFocus,
}: SideDrawerProps) {
  const placementClassName = underNavbar ? 'inset-x-0 top-12 bottom-0 z-40' : 'inset-0 z-50'

  return (
    <Drawer.Portal>
      <Drawer.Backdrop
        className={twJoin(
          'bg-system-tokens-overlay transition-base fixed transition-opacity',
          placementClassName,
          'opacity-[calc(1-var(--drawer-swipe-progress))]',
          'data-ending-style:opacity-0 data-starting-style:opacity-0 data-swiping:duration-0',
          'data-swipe-dismiss:duration-[calc(var(--drawer-swipe-strength)*400ms)]',
        )}
      />
      <Drawer.Viewport
        className={twJoin(
          'fixed flex items-stretch',
          placementClassName,
          side === 'left' ? 'justify-start' : 'justify-end',
        )}
        onClick={event => {
          if (event.target === event.currentTarget) onClose()
        }}
      >
        <Drawer.Popup
          initialFocus={initialFocus}
          finalFocus={finalFocus}
          className={twMerge(
            'flex h-full touch-auto flex-col overflow-hidden overscroll-contain outline-none',
            'transition-base transform-[translateX(var(--drawer-swipe-movement-x))] transition-transform',
            'data-swipe-dismiss:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-swiping:duration-0',
            side === 'left'
              ? 'data-ending-style:transform-[translateX(-100%)] data-starting-style:transform-[translateX(-100%)]'
              : 'data-ending-style:transform-[translateX(100%)] data-starting-style:transform-[translateX(100%)]',
            className,
          )}
        >
          {children}
        </Drawer.Popup>
      </Drawer.Viewport>
    </Drawer.Portal>
  )
}

export { SideDrawer }
